import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { resourceCache } from 'use-async-resource';
import { Filter, Invoice, InvoiceData } from '../models/table-models';
import { useGetInvoices as fetchInvoices } from './clients-hooks';

const emptyInvoices: Invoice[] = [];

export const useRequestInvoices = (
    branchId: number | undefined,
    clientId: number | undefined,
    enabled: boolean,
    currentPage: number,
    pageSize: number,
) => {
    // Branch requests are unpaginated; client pagination must not reload them.
    const page = branchId ? undefined : currentPage;
    const size = branchId ? undefined : pageSize;
    const ownerId = branchId ? undefined : clientId;
    const scope = enabled && (branchId || ownerId)
        ? JSON.stringify([branchId, ownerId, page, size]) : undefined;
    const params = useMemo(() => ({
        filter: { id: branchId ? String(branchId) : undefined } as Filter,
        'pager.page': page,
        'pager.itemsPerPage': size,
    }), [branchId, page, size]);
    const current = useRef<string>();
    const sequence = useRef(0);
    const [result, setResult] = useState<{
        scope: string;
        paid: InvoiceData<Invoice[]>;
        unpaid: InvoiceData<Invoice[]>;
    }>();
    const [failedScope, setFailedScope] = useState<string>();

    // Invalidate before passive effects, including on close and unmount.
    useLayoutEffect(() => {
        current.current = scope;
        return () => {
            current.current = undefined;
            sequence.current += 1;
        };
    }, [scope]);

    const reload = useCallback(async () => {
        if (!scope || current.current !== scope) return;
        const request = ++sequence.current;
        resourceCache(fetchInvoices).clear();
        try {
            const [paid, unpaid] = await Promise.all([
                fetchInvoices(params, true, ownerId),
                fetchInvoices(params, false, ownerId),
            ]);
            if (current.current !== scope || sequence.current !== request) return;
            setResult({ scope, paid: { ...paid, paidInvoices: sortInvoicesNewestFirst(paid.paidInvoices) }, unpaid });
            setFailedScope(undefined);
        } catch (error) {
            if (current.current === scope && sequence.current === request) {
                console.error('Failed to load request invoices', error);
                setFailedScope(scope);
            }
        }
    }, [scope, params, ownerId]);

    useEffect(() => { void reload(); }, [reload]);

    const visible = scope && result?.scope === scope ? result : undefined;
    return {
        paidInvoices: branchId ? visible?.paid.paidInvoices ?? emptyInvoices : emptyInvoices,
        unpaidInvoices: branchId ? visible?.unpaid.unpaidInvoices ?? emptyInvoices : emptyInvoices,
        paidInvoicesForClient: branchId ? undefined : visible?.paid,
        unpaidInvoicesForClient: branchId ? undefined : visible?.unpaid,
        // An empty list after a failed request must not look like "no invoices".
        failed: Boolean(scope && failedScope === scope),
        loading: Boolean(scope && !visible && failedScope !== scope),
        reload,
    };
};

const getInvoiceDateValue = (invoice: Invoice) => {
    const dateCandidates = [
        invoice.document?.invoiceDate,
        invoice.creationDate,
        invoice.duePaymentDate,
        invoice.fromDate,
    ];
    for (const candidate of dateCandidates) {
        if (!candidate) continue;
        const timestamp = new Date(candidate).getTime();
        if (Number.isFinite(timestamp)) return timestamp;
    }
    return 0;
};

const getInvoiceOrderValue = (invoice: Invoice) => Number(
    invoice.document?.invoiceNumber ?? invoice.invoiceNumber ?? invoice.document?.documentNumber ?? invoice.id ?? 0
);

const sortInvoicesNewestFirst = (invoices: Invoice[]) => [...invoices].sort((first, second) =>
    getInvoiceDateValue(second) - getInvoiceDateValue(first) || getInvoiceOrderValue(second) - getInvoiceOrderValue(first)
);
